import type { Accessor } from "solid-js";
import { onCleanup, createEffect, createSignal } from "solid-js";
import type { InfiniteData, UseInfiniteQueryResult } from "@tanstack/solid-query";
import logger from "./logger.ts";
import { isNil } from "lodash-es";

export const useInfiniteScrollWithQuery = <TData>(
  query: UseInfiniteQueryResult<InfiniteData<TData>>,
  scrollRef: Accessor<HTMLDivElement | undefined>,
  loadMorePxFromBottom = 100,
) => {
  const attemptToLoadMore = () => {
    if (query.isFetching || query.isFetchingNextPage || !query.hasNextPage) {
      return;
    }
    query.fetchNextPage().catch((e: Error) => logger.error("Failed to load next page of query", e));
  };

  const { loadedEnoughToFill } = useLoadEnoughToFill(query, scrollRef, attemptToLoadMore);
  const onScrollRefScroll = (event: Event) => {
    if (query.isFetching || query.isFetchingNextPage || !query.hasNextPage) {
      return;
    }

    const el = event.target as HTMLDivElement;
    if (el.scrollHeight <= el.clientHeight) {
      return;
    }

    const difference = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (difference < loadMorePxFromBottom) {
      attemptToLoadMore();
    }
  };

  createEffect(() => {
    const scrollRefValue = scrollRef();
    if (isNil(scrollRefValue)) {
      return;
    }

    scrollRefValue.addEventListener("scroll", onScrollRefScroll);
    onCleanup(() => {
      scrollRefValue.removeEventListener("scroll", onScrollRefScroll);
    });
  });

  return { query, loadedEnoughToFill, attemptToLoadMore };
};

const useLoadEnoughToFill = <TData>(
  query: UseInfiniteQueryResult<InfiniteData<TData>>,
  scrollRef: Accessor<HTMLDivElement | undefined>,
  attemptToLoadMore: () => void,
) => {
  const [loadedEnoughToFill, setLoadedEnoughToFill] = createSignal(false);
  createEffect(() => {
    if (loadedEnoughToFill()) {
      return;
    }
    if (query.isFetching) {
      return;
    }
    const scrollRefValue = scrollRef();
    if (isNil(scrollRefValue)) {
      return;
    }
    if (scrollRefValue.scrollHeight > scrollRefValue.clientHeight) {
      setLoadedEnoughToFill(true);
      return;
    }
    if (query.hasNextPage) {
      attemptToLoadMore();
      return;
    }

    setLoadedEnoughToFill(true);
  });

  return { loadedEnoughToFill };
};
